/**
 * Site Feedback vocabulary shared by the embed widget and Dash.
 *
 * Everything under `site-feedback/shared/` must stay browser-safe and
 * dependency-free: the widget bundle imports these modules directly, so no
 * `server-only`, Next, React, or other `@/lib` imports belong here.
 */

export const TICKET_TYPES = ["bug", "content", "improvement"] as const;
export type TicketType = (typeof TICKET_TYPES)[number];

export const TICKET_TYPE_LABELS: Record<TicketType, string> = {
  bug: "Bug",
  content: "Content change",
  improvement: "Improvement",
};

/** One-line hints shown under each type in the widget form. */
export const TICKET_TYPE_HINTS: Record<TicketType, string> = {
  bug: "Something is broken or behaves wrongly",
  content: "Wording, spelling, images or other content",
  improvement: "A change or idea that would make it better",
};

export function isTicketType(value: unknown): value is TicketType {
  return typeof value === "string" && (TICKET_TYPES as readonly string[]).includes(value);
}

export const AUDIENCES = ["internal", "client"] as const;
export type Audience = (typeof AUDIENCES)[number];

export function isAudience(value: unknown): value is Audience {
  return value === "internal" || value === "client";
}

/** Annotation palette. Index 0 is the default. Hex only (it lands in SVG). */
export const ANNOTATION_COLORS = ["#E5372C", "#F2A900", "#1E6FD9", "#1F2026"] as const;

/** Caps applied on both sides: the widget trims, the server re-checks. */
export const LIMITS = {
  descriptionChars: 5000,
  nameChars: 120,
  emailChars: 254,
  pageTitleChars: 300,
  urlChars: 2000,
  consoleEntries: 50,
  networkFailures: 30,
  routeTrail: 10,
  entryChars: 2000,
  /** Total JSON size of the payload field (everything except the images). */
  payloadBytes: 256 * 1024,
  screenshotBytes: 8 * 1024 * 1024,
  thumbBytes: 300 * 1024,
  imageMaxSide: 4096,
  requestBytes: 12 * 1024 * 1024,
} as const;

/** URL fragment key carried by activation links: https://site/#spark-feedback=<token> */
export const ACTIVATION_FRAGMENT_KEY = "spark-feedback";

/** `#spark-feedback=reset` clears this browser's stored link and "stop showing" choice. */
export const ACTIVATION_RESET_VALUE = "reset";

/**
 * Signed admin activation token Dash mints for the SPARK Site Tools plugin to
 * hand logged-in WordPress administrators (`a1.<claim>.<signature>`). It is
 * short-lived and origin-bound; the server verifies it, this only shapes it.
 */
export const ADMIN_ACTIVATION_RE = /^a1\.[A-Za-z0-9_-]{8,512}\.[A-Za-z0-9_-]{43}$/;

/** Attribute on the widget's host element; audits exclude it, captures skip it. */
export const WIDGET_ROOT_ATTRIBUTE = "data-spark-feedback-root";

/** Class site owners can add to keep an element out of every capture. */
export const EXCLUDE_CLASS = "spark-feedback-exclude";

/**
 * Class on the widget's host element. Replays record the host only as an
 * empty placeholder (it is blocked by WIDGET_ROOT_ATTRIBUTE), and the player
 * hides that placeholder by this class.
 */
export const WIDGET_HOST_CLASS = "spark-feedback-host";
